import type { AxiosError } from 'axios';
import humps from 'humps';
import { createMutation } from 'react-query-kit';

import { client, throwFriendlyApiError } from '../common';
import type { Reservation } from './types';

type Variables = {
  id: number;
  emails: string[];
  paymentIntentId?: string;
};

export type AdditionalDriverPayment = {
  stripeSecret: string;
  customerSessionSecret?: string;
  ephemeralKey?: string;
  stripeCustomerId?: string;
  paymentIntentId: string;
  amount: number;
  lineItems: {
    email: string;
    dailyRate: number;
    days: number;
    amount: number;
  }[];
};

export class AdditionalDriverPaymentRequired extends Error {
  payment: AdditionalDriverPayment;

  constructor(payment: AdditionalDriverPayment) {
    super('Payment required');
    this.payment = payment;
  }
}

export class AdditionalDriverInviteErrors extends Error {
  errorsByEmail: Record<string, string>;

  constructor(errorsByEmail: Record<string, string>) {
    super('Some invites are invalid');
    this.errorsByEmail = errorsByEmail;
  }
}

export const useAddAdditionalDriver = createMutation<
  { data: Reservation },
  Variables,
  AxiosError
>({
  mutationFn: async (variables) =>
    client({
      url: `v2/reservations/${variables.id}/additional-drivers`,
      method: 'POST',
      data: {
        emails: variables.emails,
        payment_intent_id: variables.paymentIntentId,
      },
    })
      .then((response) => response.data)
      .catch((err: any) => {
        if (err.response?.status === 402) {
          throw new AdditionalDriverPaymentRequired(
            humps.camelizeKeys(
              err.response.data.payment
            ) as AdditionalDriverPayment
          );
        }

        // Per-email validation errors are objects; other failures are strings.
        const errors = err.response?.data?.errors;
        if (Array.isArray(errors) && typeof errors[0] === 'object') {
          throw new AdditionalDriverInviteErrors(
            Object.fromEntries(
              errors.map((e: { email: string; message: string }) => [
                e.email,
                e.message,
              ])
            )
          );
        }

        throwFriendlyApiError(err, 'invite drivers');
      }),
});
